import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { employeesAPI } from "../api/client";
import { toast } from "react-toastify";
import LoadingButton from "../components/LoadingButton";
import Breadcrumb from "../components/Breadcrumb";
import { mayPerformWithRole } from "../utils/userAccess";

const TIME_SHEET_OPTIONS = ["Yes", "No"];

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const fromHeadcount = location.state?.fromHeadcount;
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const canEdit = mayPerformWithRole(user?.role, "employee", "edit", "employee.edit");
  const [form, setForm] = useState({
    incentive_type: "", incentive_amount: 0, bonus_type: "", bonus_amount: 0,
    project_allocation: "", asset_assigned: "", reporting_notes: "", remarks: "",
    group_head_name: "", business_unit_head_name: "", total_experience: "",
    current_experience: "", parent_company_name: "", time_sheet: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await employeesAPI.getById(id);
        setEmployee(res.data);
        setForm({
          incentive_type: res.data.incentive_type || "",
          incentive_amount: res.data.incentive_amount || 0,
          bonus_type: res.data.bonus_type || "",
          bonus_amount: res.data.bonus_amount || 0,
          project_allocation: res.data.project_allocation || "",
          asset_assigned: res.data.asset_assigned || "",
          reporting_notes: res.data.reporting_notes || "",
          remarks: res.data.remarks || "",
          group_head_name: res.data.group_head_name || "",
          business_unit_head_name: res.data.business_unit_head_name || "",
          parent_company_name: res.data.parent_company_name || "",
          total_experience: res.data.total_experience ?? "",
          current_experience: res.data.current_experience ?? "",
          time_sheet: res.data.time_sheet || "",
        });
      } catch (err) {
        toast.error("Failed to load employee");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const parseExperience = (value) => {
    if (value === "" || value === null || value === undefined) return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : NaN;
  };

  const validateExperiences = () => {
    const previous = parseExperience(form.current_experience);
    if (Number.isNaN(previous)) {
      toast.error("Previous experience must be a valid number");
      return false;
    }
    if (previous !== null && previous < 0) {
      toast.error("Previous experience cannot be negative");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validateExperiences()) return;
    setSaving(true);
    try {
      await employeesAPI.update(id, form);
      toast.success("Employee updated successfully");
      setEditMode(false);
      const res = await employeesAPI.getById(id);
      setEmployee(res.data);
    } catch (err) {
      toast.error("Failed to update employee");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <style>{css}</style>
        <div className="employee-detail-page">
          <div className="employee-card loading-card">
            <div className="loading-spinner"></div>
            <h3>Loading employee details...</h3>
          </div>
        </div>
      </>
    );
  }

  if (!employee) {
    return (
      <>
        <style>{css}</style>
        <div className="employee-detail-page">
          <div className="employee-card">
            <p>Employee not found</p>
            <button className="btn-back" onClick={() => navigate('/employees')}>Back to List</button>
          </div>
        </div>
      </>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: name === 'incentive_amount' || name === 'bonus_amount' ? Number(value) : value }));
  };

  function renderField(label, name, textarea = false, options = {}) {
    const { type, step, min, helperText } = options;
    const inputType = type || (name.includes('amount') ? 'number' : 'text');
    const numeric = inputType === 'number';
    return (
      <div className="detail-field">
        <label className="field-label">
          <span className="field-icon"></span>
          {label}
        </label>
        {editMode ? (
          textarea ? (
            <textarea
              name={name}
              value={form[name] || ''}
              onChange={handleChange}
              rows={3}
              className="field-textarea"
              placeholder={`Enter ${label.toLowerCase()}`}
            />
          ) : (
            <>
              <input
                name={name}
                type={inputType}
                step={numeric ? (step || 'any') : undefined}
                min={numeric ? (min ?? 0) : undefined}
                value={form[name] ?? ''}
                onChange={handleChange}
                className={`field-input ${numeric ? 'no-spinner' : ''}`}
                placeholder={`Enter ${label.toLowerCase()}`}
              />
              {helperText && <span className="field-helper">{helperText}</span>}
            </>
          )
        ) : (
          <div className="field-value">
            {employee[name] !== null && employee[name] !== undefined && employee[name] !== '' ? String(employee[name]) : '—'}
          </div>
        )}
      </div>
    );
  }

  function renderSelectField(label, name, choices, options = {}) {
    const { helperText, placeholder = "-- Select --" } = options;
    return (
      <div className="detail-field">
        <label className="field-label">
          <span className="field-icon"></span>
          {label}
        </label>
        {editMode ? (
          <>
            <select
              name={name}
              value={form[name] ?? ""}
              onChange={handleChange}
              className="field-input"
            >
              <option value="">{placeholder}</option>
              {choices.map((choice) => (
                <option key={choice} value={choice}>{choice}</option>
              ))}
            </select>
            {helperText && <span className="field-helper">{helperText}</span>}
          </>
        ) : (
          <div className="field-value">
            {employee[name] !== null && employee[name] !== undefined && employee[name] !== '' ? String(employee[name]) : '—'}
          </div>
        )}
      </div>
    );
  }

  function renderReadonlyField(label, value) {
    return (
      <div className="detail-field">
        <label className="field-label">
          <span className="field-icon"></span>
          {label}
        </label>
        <div className="field-value readonly">
          {value || '—'}
        </div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <>
      <style>{css}</style>

      <Breadcrumb items={[{ label: "Employees", to: "/employees" }, { label: employee?.name || "Employee Detail" }]} />
      <div className="employee-detail-page">
        <div className="employee-card">

          <div className="card-header">
            <div className="header-left">
              <div className="avatar-large">
                {(employee.name || 'E')[0].toUpperCase()}
              </div>
              <div className="header-info">
                <h1 className="employee-name">{employee.name}</h1>
                <p className="employee-id">ID: #{employee.employee_id}</p>
              </div>
            </div>

            <div className="header-actions">
              {fromHeadcount ? (
                <button className="btn-back" onClick={() => navigate('/headcount')}>
                  ← Back to HC Board
                </button>
              ) : (
                <button className="btn-back" onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/employees'); }}>
                  Back
                </button>
              )}

              {!editMode && canEdit ? (
                <button className="btn-edit" onClick={() => setEditMode(true)}>
                  Edit
                </button>
              ) : (
                <>
                  <LoadingButton className="btn-save" onClick={handleSave} loading={saving} loadingText="Saving...">
                    Save
                  </LoadingButton>
                  <button
                    className="btn-cancel"
                    onClick={() => {
                      setEditMode(false);
                      setForm({
                        incentive_type: employee.incentive_type || "",
                        incentive_amount: employee.incentive_amount || 0,
                        bonus_type: employee.bonus_type || "",
                        bonus_amount: employee.bonus_amount || 0,
                        project_allocation: employee.project_allocation || "",
                        asset_assigned: employee.asset_assigned || "",
                        reporting_notes: employee.reporting_notes || "",
                        remarks: employee.remarks || "",
                        group_head_name: employee.group_head_name || "",
                        business_unit_head_name: employee.business_unit_head_name || "",
                        parent_company_name: employee.parent_company_name || "",
                        total_experience: employee.total_experience ?? "",
                        current_experience: employee.current_experience ?? "",
                        time_sheet: employee.time_sheet || "",
                      });
                    }}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="meta-info-section">
            <div className="meta-item">
              <span className="meta-icon"></span>
              <div>
                <span className="meta-label">Company</span>
                <span className="meta-value">{employee.company || '—'}</span>
              </div>
            </div>
            <div className="meta-item">
              <span className="meta-icon"></span>
              <div>
                <span className="meta-label">Department</span>
                <span className="meta-value">{employee.department || '—'}</span>
              </div>
            </div>
            <div className="meta-divider"></div>
            <div className="meta-item">
              <span className="meta-icon"></span>
              <div>
                <span className="meta-label">Designation</span>
                <span className="meta-value">{employee.designation || '—'}</span>
              </div>
            </div>
            <div className="meta-divider"></div>
            <div className="meta-item">
              <span className="meta-icon"></span>
              <div>
                <span className="meta-label">Joining Date</span>
                <span className="meta-value">{formatDate(employee.joining_date)}</span>
              </div>
            </div>
            <div className="meta-divider"></div>
            <div className="meta-item">
              <span className="meta-icon"></span>
              <div>
                <span className="meta-label">Exit Date</span>
                <span className="meta-value">{formatDate(employee.exit_date)}</span>
              </div>
            </div>
          </div>

          <div className="section">
            <h3 className="section-title">
              Zoho Information
              <span className="source-badge">Source: Zoho People (Read Only)</span>
            </h3>
            <div className="section-grid">
              {renderReadonlyField('Employee ID', employee.employee_id)}
              {renderReadonlyField('Name', employee.name)}
              {renderReadonlyField('Resource Email ID', employee.resource_email_id || employee.email)}
              {renderReadonlyField('Mobile Number', employee.mobile_number || employee.phone)}
              {renderReadonlyField('Department', employee.department)}
              {renderReadonlyField('Division', employee.division)}
              {renderReadonlyField('Designation', employee.designation)}
              {renderReadonlyField('Reporting Manager', employee.reporting_manager || employee.manager)}
              {renderReadonlyField('Reporting Manager Email ID', employee.reporting_manager_email)}
              {renderReadonlyField('Employment Type', employee.employment_type)}
              {renderReadonlyField('Source of Hire', employee.source_of_hire)}
              {renderReadonlyField('Joining Date', formatDate(employee.joining_date))}
              {renderReadonlyField('Exit Date', formatDate(employee.exit_date))}
              {renderReadonlyField('Seating Location', employee.seating_location)}
              {renderReadonlyField('Location', employee.location)}
              {renderReadonlyField('Company Name', employee.company)}
              {renderReadonlyField('Business Unit', employee.business_unit)}
              {renderReadonlyField('Gender', employee.gender)}
              {renderReadonlyField('Status', employee.status)}
            </div>
          </div>

          <div className="section">
            <h3 className="section-title">
              HR Portal Information
              <span className="source-badge editable">Editable</span>
            </h3>
            <div className="section-grid">
              {renderField('Incentive Type', 'incentive_type')}
              {renderField('Incentive Amount', 'incentive_amount')}
              {renderField('Bonus Type', 'bonus_type')}
              {renderField('Bonus Amount', 'bonus_amount')}
              {renderField('Project Allocation', 'project_allocation')}
              {renderField('Asset Assigned', 'asset_assigned')}
              {renderField('Group Head Name', 'group_head_name')}
              {renderField('Business Unit Head Name', 'business_unit_head_name')}
              {renderField('Parent Company Name', 'parent_company_name')}
              {renderField('Previous Experience', 'current_experience', false, { type: 'number', step: '0.01', min: '0', helperText: 'Years (e.g., 2.5)' })}
              {renderSelectField('Time Sheet', 'time_sheet', TIME_SHEET_OPTIONS, { helperText: 'Select Yes or No' })}
            </div>
          </div>

          {editMode && (
            <div className="section">
              <h3 className="section-title">Additional Information</h3>
              <div className="section-grid">
                {renderField('Reporting Notes', 'reporting_notes', true)}
                {renderField('Remarks', 'remarks', true)}
              </div>
            </div>
          )}

          {!editMode && (employee.reporting_notes || employee.remarks) && (
            <div className="section">
              <h3 className="section-title">Additional Information</h3>
              <div className="section-grid">
                {employee.reporting_notes && renderReadonlyField('Reporting Notes', employee.reporting_notes)}
                {employee.remarks && renderReadonlyField('Remarks', employee.remarks)}
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}

const css = `
.employee-detail-page {
  min-height: 100vh;
  background: #F8FAFC;
  padding: 24px;
  font-family: var(--font-sans);
  animation: fadeIn 0.5s ease-out;
}
.employee-card {
  background: #FFFFFF;
  max-width: 1200px;
  margin: 0 auto;
  border-radius: 12px;
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  animation: slideUp 0.6s ease-out;
}
.loading-card {
  text-align: center;
  padding: 40px 24px;
}
.loading-spinner {
  font-size: 36px;
  margin-bottom: 12px;
  animation: spin 2s linear infinite;
}
.card-header {
  background: linear-gradient(135deg, #1E40AF, #1e3a8a);
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
  gap: 14px;
  flex-wrap: wrap;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.avatar-large {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3B82F6, #0EA5E9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: bold;
  color: white;
  box-shadow: var(--shadow-md);
  flex-shrink: 0;
}
.header-info {
  flex: 1;
}
.employee-name {
  margin: 0 0 4px 0;
  font-size: 24px;
  font-weight: 700;
  color: white;
  font-family: var(--font-sans);
}
.employee-id {
  margin: 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}
.header-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
button {
  padding: 8px 16px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  font-family: var(--font-sans);
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.btn-back {
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 2px solid rgba(255, 255, 255, 0.4);
  transition: all 0.2s ease;
}
.btn-back:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}
.btn-edit {
  background: linear-gradient(135deg, #3B82F6, #2563EB);
  color: white;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}
.btn-edit:hover {
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
  transform: translateY(-2px);
}
.btn-save {
  background: linear-gradient(135deg, #10B981, #059669);
  color: white;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}
.btn-save:hover {
  box-shadow: 0 6px 16px rgba(16, 185, 129, 0.4);
  transform: translateY(-2px);
}
.btn-cancel {
  background: linear-gradient(135deg, #EF4444, #DC2626);
  color: white;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.3);
}
.btn-cancel:hover {
  box-shadow: 0 6px 16px rgba(239, 68, 68, 0.4);
  transform: translateY(-2px);
}
.meta-info-section {
  display: flex;
  padding: 12px 24px;
  background: #F8FAFC;
  border-bottom: 1px solid #E2E8F0;
  gap: 24px;
  align-items: center;
  flex-wrap: wrap;
}
.meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
}
.meta-icon {
  font-size: 18px;
}
.meta-label {
  display: block;
  font-size: 11px;
  color: #64748B;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}
.meta-value {
  display: block;
  font-size: 13px;
  color: #0F172A;
  font-weight: 500;
  margin-top: 1px;
}
.meta-divider {
  width: 1px;
  height: 28px;
  background: #CBD5E1;
}
.section {
  padding: 20px;
  border-bottom: 1px solid #E2E8F0;
}
.section:last-child {
  border-bottom: none;
}
.section-title {
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 700;
  color: #0F172A;
  font-family: var(--font-sans);
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.source-badge {
  font-size: 11px;
  font-weight: 600;
  color: #6B7280;
  background: #F5F8FC;
  padding: 3px 10px;
  border-radius: 10px;
  border: 1px solid #D9E2EF;
  white-space: nowrap;
}
.source-badge.editable {
  background: #E3F2FD;
  color: #0F4C81;
  border-color: #BBDEFB;
}
.section-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 12px;
}
.detail-field {
  animation: fadeInUp 0.4s ease-out;
}
.field-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #64748B;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
  font-family: var(--font-sans);
}
.field-icon {
  font-size: 14px;
}
.field-value {
  padding: 8px 12px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  font-size: 14px;
  color: #0F172A;
  font-weight: 500;
  min-height: 36px;
  display: flex;
  align-items: center;
}
.field-value.readonly {
  background: #F0F4FF;
  border-color: #CBD5E1;
}
.field-input {
  width: 100%;
  padding: 8px 12px;
  border: 2px solid #CBD5E1;
  border-radius: 8px;
  font-size: 14px;
  font-family: var(--font-sans);
  transition: all 0.3s ease;
  box-sizing: border-box;
}
.field-input:focus {
  outline: none;
  border-color: #1E40AF;
  box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
}
.field-helper {
  display: block;
  margin-top: 4px;
  font-size: 11px;
  color: #64748B;
  font-family: var(--font-sans);
}
.field-textarea {
  width: 100%;
  padding: 8px 12px;
  border: 2px solid #CBD5E1;
  border-radius: 8px;
  font-size: 14px;
  font-family: var(--font-sans);
  transition: all 0.3s ease;
  resize: vertical;
  box-sizing: border-box;
}
.field-textarea:focus {
  outline: none;
  border-color: #1E40AF;
  box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes slideUp {
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@media (max-width: 768px) {
  .employee-detail-page { padding: 16px; }
  .card-header { flex-direction: column; align-items: flex-start; padding: 16px; }
  .header-left { flex-direction: column; align-items: flex-start; }
  .header-actions { width: 100%; }
  .header-actions button { flex: 1; justify-content: center; }
  .employee-name { font-size: 22px; }
  .avatar-large { width: 52px; height: 52px; font-size: 24px; }
  .section { padding: 16px; }
  .section-grid { grid-template-columns: 1fr; }
  .meta-info-section { flex-direction: column; align-items: flex-start; padding: 12px 16px; gap: 12px; }
  .meta-divider { display: none; }
}
input[type="number"].no-spinner::-webkit-inner-spin-button,
input[type="number"].no-spinner::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
input[type="number"].no-spinner {
  -moz-appearance: textfield;
}
`;
